import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import it from '../locales/it.json';

type Translations = typeof it;

// Italian is bundled statically; every other locale is fetched lazily on first
// use. `dir: 'rtl'` flips the document for right-to-left scripts (Arabic).
export const SUPPORTED_LANGUAGES = [
  { code: 'it', label: 'Italiano', dir: 'ltr' },
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'es', label: 'Español', dir: 'ltr' },
  { code: 'ro', label: 'Română', dir: 'ltr' },
  { code: 'uk', label: 'Українська', dir: 'ltr' },
  { code: 'bn', label: 'বাংলা', dir: 'ltr' },
  { code: 'zh', label: '简体中文', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code) as string[];

export function getLanguageDir(lng: string | undefined): 'ltr' | 'rtl' {
  const base = (lng ?? '').split('-')[0];
  return SUPPORTED_LANGUAGES.find((l) => l.code === base)?.dir ?? 'ltr';
}

function buildResources(
  translations: Translations,
): Record<string, Translations | Translations[keyof Translations]> {
  const namespaces: Record<string, Translations | Translations[keyof Translations]> = {
    translation: translations,
  };
  for (const [key, value] of Object.entries(translations) as [
    keyof Translations,
    Translations[keyof Translations],
  ][]) {
    namespaces[key] = value;
  }
  return namespaces;
}

function applyResources(lng: string, translations: Translations): void {
  const resources = buildResources(translations);
  const isHmr = Boolean(import.meta.hot);
  for (const [ns, data] of Object.entries(resources)) {
    // deep merge; overwrite during HMR so new/changed keys propagate to the
    // already-running session without a manual page reload.
    i18n.addResourceBundle(lng, ns, data, true, isHmr);
  }
}

// Static `import()` per language so Bun.build can resolve each locale and split
// it into its own lazily-fetched chunk.
function importLocale(code: string): Promise<{ default: Translations }> {
  // Locales legitimately drift from `it`'s literal string values, so a direct
  // cast on the JSON module trips TS's literal-overlap heuristic. Narrow at the
  // dynamic-import boundary via `unknown` instead.
  const typed = (m: { default: unknown }): { default: Translations } => ({
    default: m.default as Translations,
  });
  switch (code) {
    case 'en':
      return import('../locales/en.json').then(typed);
    case 'es':
      return import('../locales/es.json').then(typed);
    case 'ro':
      return import('../locales/ro.json').then(typed);
    case 'uk':
      return import('../locales/uk.json').then(typed);
    case 'bn':
      return import('../locales/bn.json').then(typed);
    case 'zh':
      return import('../locales/zh.json').then(typed);
    case 'ar':
      return import('../locales/ar.json').then(typed);
    default:
      return Promise.resolve({ default: it });
  }
}

const loaders = new Map<string, Promise<void>>();
async function loadLanguage(code: string): Promise<void> {
  if (code === 'it' || i18n.hasResourceBundle(code, 'translation')) {
    return;
  }
  const existing = loaders.get(code);
  if (existing) {
    return existing;
  }
  const loader = importLocale(code)
    .then((mod) => {
      applyResources(code, mod.default);
    })
    .catch((err) => {
      loaders.delete(code);
      throw err;
    });
  loaders.set(code, loader);
  return loader;
}

// Direction the document was rendered with at mount. Emotion cache + MUI theme
// direction are fixed for the lifetime of the page (see App.tsx), so crossing
// the LTR↔RTL boundary requires a full reload to re-create them consistently.
let mountedDir: 'ltr' | 'rtl' | null = null;

function applyDocumentDir(lng: string | undefined): void {
  if (typeof document === 'undefined') {
    return;
  }
  const base = (lng ?? 'it').split('-')[0];
  const dir = getLanguageDir(base);
  document.documentElement.lang = base;
  document.documentElement.dir = dir;
  if (mountedDir === null) {
    mountedDir = dir;
  }
}

const initPromise = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      it: buildResources(it),
    },
    defaultNS: 'translation',
    fallbackLng: 'it',
    supportedLngs: SUPPORTED_CODES,
    nonExplicitSupportedLngs: true,
    partialBundledLanguages: true,
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'prospero_language',
    },
    interpolation: {
      escapeValue: false,
    },
  });

// Intercept changeLanguage to lazy-load the target bundle before switching.
// If the bundle fails to load the language still switches — i18next falls back
// to Italian translations, but the UI language code and direction are correct.
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (async (lng?: string, ...rest: unknown[]) => {
  const base = (lng ?? '').split('-')[0];
  if (SUPPORTED_CODES.includes(base)) {
    try {
      await loadLanguage(base);
    } catch {
      // Bundle fetch may fail (network, CDN, chunk split issue). Proceed with
      // the language switch anyway — i18next will use fallback translations.
    }
  }
  return originalChangeLanguage(lng as never, ...(rest as []));
}) as typeof i18n.changeLanguage;

i18n.on('languageChanged', (lng) => {
  const nextDir = getLanguageDir(lng);
  if (mountedDir !== null && nextDir !== mountedDir && typeof window !== 'undefined') {
    // Language (and thus localStorage) is already updated; reload to rebuild the
    // style pipeline in the new direction.
    window.location.reload();
    return;
  }
  applyDocumentDir(lng);
});

initPromise.then(async () => {
  const base = (i18n.language ?? 'it').split('-')[0];
  if (SUPPORTED_CODES.includes(base)) {
    try {
      await loadLanguage(base);
    } catch {
      // Non-critical: the language is already detected and set; only the
      // translation bundle is missing (will fall back to Italian).
    }
  }
  applyDocumentDir(i18n.language);
});

if (import.meta.hot) {
  import.meta.hot.accept('../locales/it.json', (mod) => {
    if (mod?.default) {
      applyResources('it', mod.default as Translations);
    }
  });
  import.meta.hot.accept('../locales/en.json', (mod) => {
    if (mod?.default) {
      applyResources('en', mod.default as Translations);
    }
  });
}

export default i18n;
